import React, { ReactNode } from 'react';

import styled from 'styled-components';

// Types
export interface SidebarLayoutProps {
  sidebar: ReactNode;
  children: ReactNode;
  header?: ReactNode;
  sidebarPosition?: 'left' | 'right';
  sidebarWidth?: string | number;
  sidebarCollapsed?: boolean;
  sidebarCollapsible?: boolean;
  gap?: string | number;
  className?: string;
  onSidebarToggle?: () => void;
  // Mobile behavior
  mobileBreakpoint?: string;
  mobileSidebarBehavior?: 'hidden' | 'overlay' | 'stack';
  // Layout variants
  variant?: 'default' | 'full-height' | 'page-content';
}

// Helper function to convert size to CSS value
const toCSS = (value: string | number): string => {
  return typeof value === 'number' ? `${value}px` : value;
};

// Styled Components
const LayoutContainer = styled.div<{
  variant: 'default' | 'full-height' | 'page-content';
  gap: string;
  mobileBreakpoint: string;
}>`
  display: flex;
  flex-direction: column;
  gap: ${({ gap }) => gap};

  ${({ variant }) => {
    switch (variant) {
      case 'full-height':
        return `
          height: 100vh;
          overflow: hidden;
        `;
      case 'page-content':
        return `
          min-height: calc(100vh - 200px);
          height: auto;
        `;
      default:
        return `
          height: 100%;
        `;
    }
  }}

  @media (max-width: ${({ mobileBreakpoint }) => mobileBreakpoint}) {
    gap: 0;
  }
`;

const HeaderSection = styled.div`
  flex-shrink: 0;
  z-index: 100;
`;

const ContentArea = styled.div<{
  sidebarPosition: 'left' | 'right';
  sidebarWidth: string;
  sidebarCollapsed: boolean;
  gap: string;
  mobileBreakpoint: string;
  mobileSidebarBehavior: 'hidden' | 'overlay' | 'stack';
}>`
  display: flex;
  flex-direction: ${({ sidebarPosition }) => (sidebarPosition === 'left' ? 'row' : 'row-reverse')};
  flex: 1;
  gap: ${({ gap }) => gap};
  min-height: 0; /* Allow content to shrink */
  overflow: hidden;

  @media (max-width: ${({ mobileBreakpoint }) => mobileBreakpoint}) {
    ${({ mobileSidebarBehavior }) => {
      switch (mobileSidebarBehavior) {
        case 'hidden':
          return 'flex-direction: column;';
        case 'overlay':
          return 'position: relative;';
        case 'stack':
          return 'flex-direction: column;';
        default:
          return 'flex-direction: column;';
      }
    }}
    gap: 0;
  }
`;

const SidebarSection = styled.div<{
  sidebarWidth: string;
  sidebarCollapsed: boolean;
  sidebarCollapsible: boolean;
  mobileBreakpoint: string;
  mobileSidebarBehavior: 'hidden' | 'overlay' | 'stack';
}>`
  flex-shrink: 0;
  width: ${({ sidebarWidth, sidebarCollapsed, sidebarCollapsible }) => (sidebarCollapsible && sidebarCollapsed ? '0px' : sidebarWidth)};

  overflow: ${({ sidebarCollapsed, sidebarCollapsible }) => (sidebarCollapsible && sidebarCollapsed ? 'hidden' : 'auto')};

  transition: width 0.3s ease-in-out;
  background-color: ${({ theme }) => theme.colors.palette.grayscale[0]};
  border-right: 1px solid ${({ theme }) => theme.colors.palette.grayscale[2]};

  /* Hide scrollbar but keep functionality */
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colors.palette.grayscale[3]} transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.palette.grayscale[3]};
    border-radius: 3px;
  }

  @media (max-width: ${({ mobileBreakpoint }) => mobileBreakpoint}) {
    ${({ mobileSidebarBehavior, sidebarCollapsed }) => {
      switch (mobileSidebarBehavior) {
        case 'hidden':
          return `
            display: ${sidebarCollapsed ? 'none' : 'block'};
            width: 100%;
            border-right: none;
            border-bottom: 1px solid ${({ theme }: any) => theme.colors.palette.grayscale[2]};
          `;
        case 'overlay':
          return `
            position: ${sidebarCollapsed ? 'absolute' : 'static'};
            top: 0;
            left: ${sidebarCollapsed ? '-100%' : '0'};
            width: 280px;
            height: 100%;
            z-index: 1000;
            box-shadow: ${sidebarCollapsed ? 'none' : '2px 0 8px rgba(0,0,0,0.1)'};
            transition: left 0.3s ease-in-out;
          `;
        case 'stack':
          return `
            width: 100%;
            max-height: ${sidebarCollapsed ? '0' : '40vh'};
            border-right: none;
            border-bottom: 1px solid ${({ theme }: any) => theme.colors.palette.grayscale[2]};
            transition: max-height 0.3s ease-in-out;
          `;
        default:
          return '';
      }
    }}
  }
`;

const MainContent = styled.div<{
  mobileBreakpoint: string;
  mobileSidebarBehavior: 'hidden' | 'overlay' | 'stack';
}>`
  flex: 1;
  min-width: 0; /* Allow content to shrink */
  overflow: auto;
  background-color: ${({ theme }) => theme.colors.palette.white};

  @media (max-width: ${({ mobileBreakpoint }) => mobileBreakpoint}) {
    ${({ mobileSidebarBehavior }) => {
      switch (mobileSidebarBehavior) {
        case 'overlay':
          return `
            position: relative;
            z-index: 1;
          `;
        default:
          return '';
      }
    }}
  }
`;

const SidebarToggleButton = styled.button<{
  sidebarPosition: 'left' | 'right';
  mobileBreakpoint: string;
}>`
  display: none;
  position: fixed;
  top: 50%;
  ${({ sidebarPosition }) => sidebarPosition}: 16px;
  transform: translateY(-50%);
  z-index: 1001;

  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: ${({ theme }) => theme.colors.palette.blues[6]};
    transform: translateY(-50%) scale(1.05);
  }

  @media (max-width: ${({ mobileBreakpoint }) => mobileBreakpoint}) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const MobileOverlay = styled.div<{
  isVisible: boolean;
  mobileBreakpoint: string;
}>`
  display: none;

  @media (max-width: ${({ mobileBreakpoint }) => mobileBreakpoint}) {
    display: ${({ isVisible }) => (isVisible ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 999;
  }
`;

// Component
export const SidebarLayout: React.FC<SidebarLayoutProps> = ({
  sidebar,
  children,
  header,
  sidebarPosition = 'left',
  sidebarWidth = '280px',
  sidebarCollapsed = false,
  sidebarCollapsible = false,
  gap = '0',
  className,
  onSidebarToggle,
  mobileBreakpoint = '768px',
  mobileSidebarBehavior = 'hidden',
  variant = 'default',
}) => {
  const gapCSS = toCSS(gap);
  const sidebarWidthCSS = toCSS(sidebarWidth);

  const handleOverlayClick = () => {
    if (mobileSidebarBehavior === 'overlay' && onSidebarToggle) {
      onSidebarToggle();
    }
  };

  const handleToggleClick = () => {
    onSidebarToggle?.();
  };

  return (
    <LayoutContainer variant={variant} gap={gapCSS} mobileBreakpoint={mobileBreakpoint} className={className}>
      {header && <HeaderSection>{header}</HeaderSection>}

      <ContentArea
        sidebarPosition={sidebarPosition}
        sidebarWidth={sidebarWidthCSS}
        sidebarCollapsed={sidebarCollapsed}
        gap={gapCSS}
        mobileBreakpoint={mobileBreakpoint}
        mobileSidebarBehavior={mobileSidebarBehavior}
      >
        <SidebarSection
          sidebarWidth={sidebarWidthCSS}
          sidebarCollapsed={sidebarCollapsed}
          sidebarCollapsible={sidebarCollapsible}
          mobileBreakpoint={mobileBreakpoint}
          mobileSidebarBehavior={mobileSidebarBehavior}
        >
          {sidebar}
        </SidebarSection>

        <MainContent mobileBreakpoint={mobileBreakpoint} mobileSidebarBehavior={mobileSidebarBehavior}>
          {children}
        </MainContent>
      </ContentArea>

      {/* Mobile toggle button */}
      {sidebarCollapsible && onSidebarToggle && (
        <SidebarToggleButton
          onClick={handleToggleClick}
          sidebarPosition={sidebarPosition}
          mobileBreakpoint={mobileBreakpoint}
          aria-label={`${sidebarCollapsed ? 'Show' : 'Hide'} sidebar`}
        >
          {sidebarPosition === 'left' ? '☰' : '☰'}
        </SidebarToggleButton>
      )}

      {/* Mobile overlay for overlay behavior */}
      {mobileSidebarBehavior === 'overlay' && <MobileOverlay isVisible={!sidebarCollapsed} onClick={handleOverlayClick} mobileBreakpoint={mobileBreakpoint} />}
    </LayoutContainer>
  );
};

SidebarLayout.displayName = 'SidebarLayout';
